import { type IEvent } from 'clients/PlatformApi';
import get from 'lodash/get';
import omit from 'lodash/omit';

/**
 * List of fields to filter client-side from `eqMustHaveFields` and `eqMustNotFields`.
 */
const FIELDS_TO_MATCH = ['subtype', 'state'];

/**
 * Strips any fields from `eqMustHaveFields` and `eqMustNotFields` that are processed
 * client-side by the EventCache.
 */
export function convertEqMustHaveFields(
  fields: { [key: string]: any } | undefined,
): { [key: string]: any } | undefined {
  if (!fields) {
    return undefined;
  }

  return omit(fields, ...FIELDS_TO_MATCH);
}

/**
 * Function to check `eqMustHaveFields` and `eqMustNotFields`.  Since the value
 * of the field can be anything, and the value in the event is not known, we
 * mostly leave these up to platform API to sort out... But, some things we
 * can take care of ourselves - for example `subtype` is always a string
 * on the event and a string in `eqMustHaveFields`, so we can sort it out
 * client side.
 */
export function checkEqMustHaveFields(
  event: IEvent,
  fields: { [key: string]: any } | undefined,
  not: boolean,
) {
  if (!fields) {
    return true;
  }

  for (const field of Object.keys(fields)) {
    const value = fields[field];

    if (FIELDS_TO_MATCH.includes(field)) {
      const match = event.subtype === value || event.state === value;
      if (!match === not) {
        // This event is OK.
      } else {
        return false;
      }
    } else {
      // Ignore all other fields.  They're handled by platform API.
    }
  }

  return true;
}

/**
 * Function to check if all fields in `eqMustNonZeroFields` exist and are not 0.
 */
export function checkEqMustNonZeroFields(event: IEvent, eqMustNonZeroFields?: readonly string[]) {
  if (!eqMustNonZeroFields) {
    return true;
  }

  return eqMustNonZeroFields.every((field) => {
    const val = get(event, field);
    return val !== undefined && val !== 0 && val !== '0';
  });
}
